// Dashboard Integration Service - Fixed Version with all required methods
// Mock AsyncStorage for environments where it's not available
let AsyncStorage;
try {
  AsyncStorage = require('@react-native-async-storage/async-storage').default;
} catch (error) {
  console.warn('AsyncStorage not available, using mock implementation');
  AsyncStorage = {
    getItem: async (key) => null,
    setItem: async (key, value) => {},
    multiGet: async (keys) => keys.map(key => [key, null]),
    removeItem: async (key) => {}
  };
}

class DashboardIntegrationService {
  constructor() {
    this.dashboardData = new Map();
    this.realTimeUpdates = new Map();
    this.analyticsData = new Map();
    this.subscribers = new Map();
    this.updateInterval = null;
    this.isInitialized = false;
  }

  // Initialize method (required by AIVideoInterviewScreen)
  async initialize() {
    try {
      console.log('🚀 Initializing Dashboard Integration Service...');
      
      // Load cached data
      await this.loadCachedData();
      
      // Initialize analytics
      await this.initializeAnalytics();
      
      // Start real-time updates
      this.startRealTimeUpdates();
      
      this.isInitialized = true;
      console.log('✅ Dashboard Integration Service initialized successfully');
      
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize Dashboard Integration Service:', error);
      return false;
    }
  }

  // Load cached data
  async loadCachedData() {
    try {
      const cachedData = await AsyncStorage.getItem('dashboard_data');
      if (cachedData) {
        const data = JSON.parse(cachedData);
        data.forEach(item => {
          this.dashboardData.set(item.id, item);
        });
      }
      console.log('📦 Dashboard cached data loaded');
    } catch (error) {
      console.error('❌ Error loading cached data:', error);
    }
  }

  // Initialize analytics
  async initializeAnalytics() {
    try {
      // Setup analytics tracking
      console.log('📊 Analytics initialized');
    } catch (error) {
      console.error('❌ Error initializing analytics:', error);
    }
  }

  // Start real-time updates
  startRealTimeUpdates() {
    try {
      // Start periodic updates
      this.updateInterval = setInterval(() => {
        this.performRealTimeUpdate();
      }, 30000); // Every 30 seconds
      
      console.log('🔄 Real-time updates started');
    } catch (error) {
      console.error('❌ Error starting real-time updates:', error);
    }
  }

  // Perform real-time update
  performRealTimeUpdate() {
    try {
      // Update metrics and notify subscribers
      const timestamp = new Date().toISOString();
      this.notifySubscribers('dashboard_updated', { timestamp });
    } catch (error) {
      console.error('❌ Error performing real-time update:', error);
    }
  }

  // Update interview metrics (required by AIVideoInterviewScreen)
  async updateInterviewMetrics(metricsData) {
    try {
      console.log('📊 Updating interview metrics:', metricsData);
      
      const metrics = {
        id: `metrics_${Date.now()}`,
        ...metricsData,
        timestamp: new Date().toISOString()
      };
      
      // Store metrics
      this.analyticsData.set(metrics.id, metrics);
      
      // Save to storage
      await this.saveAnalyticsData();
      
      // Notify subscribers
      this.notifySubscribers('interview_metrics_updated', metrics);
      
      return metrics;
    } catch (error) {
      console.error('❌ Error updating interview metrics:', error);
      throw error;
    }
  }

  // Update interview status (required by AIVideoInterviewScreen)
  async updateInterviewStatus(interviewId, status, additionalData = {}) {
    try {
      console.log('📝 Updating interview status:', { interviewId, status });
      
      const statusUpdate = {
        interviewId,
        status,
        ...additionalData,
        updatedAt: new Date().toISOString()
      };
      
      // Store status update
      this.dashboardData.set(`status_${interviewId}`, statusUpdate);
      
      // Save to storage
      await this.saveDashboardData();
      
      // Notify subscribers
      this.notifySubscribers('interview_status_updated', statusUpdate);
      
      return statusUpdate;
    } catch (error) {
      console.error('❌ Error updating interview status:', error);
      throw error;
    }
  }

  // Update question response (required by AIVideoInterviewScreen)
  async updateQuestionResponse(interviewId, responseData) {
    try {
      console.log('❓ Updating question response:', { interviewId, questionId: responseData.questionId });
      
      const response = {
        id: `response_${Date.now()}`,
        interviewId,
        ...responseData,
        timestamp: new Date().toISOString()
      };
      
      // Store response
      this.dashboardData.set(response.id, response);
      
      // Save to storage
      await this.saveDashboardData();
      
      // Notify subscribers
      this.notifySubscribers('question_response_updated', response);
      
      return response;
    } catch (error) {
      console.error('❌ Error updating question response:', error);
      throw error;
    }
  }

  // Update interview completion (required by AIVideoInterviewScreen)
  async updateInterviewCompletion(interviewId, results) {
    try {
      console.log('🏆 Updating interview completion:', { interviewId, score: results.overallScore });
      
      const completion = {
        id: `completion_${interviewId}`,
        interviewId,
        results,
        completedAt: new Date().toISOString(),
        status: 'completed'
      };
      
      // Store completion data
      this.dashboardData.set(completion.id, completion);
      
      // Update analytics
      await this.updateCompletionAnalytics(completion);
      
      // Save to storage
      await this.saveDashboardData();
      
      // Notify subscribers
      this.notifySubscribers('interview_completed', completion);
      
      return completion;
    } catch (error) {
      console.error('❌ Error updating interview completion:', error);
      throw error;
    }
  }

  // Update completion analytics
  async updateCompletionAnalytics(completion) {
    try {
      const analytics = {
        totalInterviews: this.getTotalInterviews() + 1,
        averageScore: this.calculateAverageScore(completion.results.overallScore),
        completionRate: this.calculateCompletionRate(),
        lastCompleted: completion.completedAt
      };
      
      this.analyticsData.set('completion_analytics', analytics);
      await this.saveAnalyticsData();
      
      console.log('📈 Completion analytics updated');
    } catch (error) {
      console.error('❌ Error updating completion analytics:', error);
    }
  }

  // Get total interviews
  getTotalInterviews() {
    return Array.from(this.dashboardData.values())
      .filter(item => item.id && item.id.startsWith('completion_')).length;
  }

  // Calculate average score
  calculateAverageScore(newScore) {
    const completions = Array.from(this.dashboardData.values())
      .filter(item => item.id && item.id.startsWith('completion_') && item.results);
    
    if (completions.length === 0) return newScore;
    
    const totalScore = completions.reduce((sum, completion) => sum + completion.results.overallScore, 0) + newScore;
    return Math.round(totalScore / (completions.length + 1));
  }

  // Calculate completion rate
  calculateCompletionRate() {
    const started = Array.from(this.dashboardData.values())
      .filter(item => item.interviewId).length;
    const completed = Array.from(this.dashboardData.values())
      .filter(item => item.id && item.id.startsWith('completion_')).length;
    
    return started > 0 ? Math.round((completed / started) * 100) : 0;
  }

  // Subscribe to events
  subscribe(event, callback) {
    if (!this.subscribers.has(event)) {
      this.subscribers.set(event, []);
    }
    this.subscribers.get(event).push(callback);
  }

  // Unsubscribe from events
  unsubscribe(event, callback) {
    if (this.subscribers.has(event)) {
      const callbacks = this.subscribers.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // Notify subscribers
  notifySubscribers(event, data) {
    if (this.subscribers.has(event)) {
      this.subscribers.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in subscriber callback for ${event}:`, error);
        }
      });
    }
  }

  // Get dashboard data
  getDashboardData(candidateId) {
    return {
      metrics: this.getMetrics(candidateId),
      recentActivity: this.getRecentActivity(candidateId),
      analytics: this.getAnalytics(candidateId),
      status: this.isInitialized ? 'active' : 'initializing'
    };
  }

  // Get metrics
  getMetrics(candidateId) {
    return {
      totalInterviews: this.getTotalInterviews(),
      averageScore: this.calculateAverageScore(0),
      completionRate: this.calculateCompletionRate(),
      lastActivity: new Date().toISOString()
    };
  }

  // Get recent activity
  getRecentActivity(candidateId) {
    return Array.from(this.dashboardData.values())
      .filter(item => item.timestamp)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 10);
  }

  // Get analytics
  getAnalytics(candidateId) {
    return this.analyticsData.get('completion_analytics') || {
      totalInterviews: 0,
      averageScore: 0,
      completionRate: 0,
      lastCompleted: null
    };
  }

  // Save dashboard data
  async saveDashboardData() {
    try {
      const dataArray = Array.from(this.dashboardData.values());
      await AsyncStorage.setItem('dashboard_data', JSON.stringify(dataArray));
    } catch (error) {
      console.error('❌ Error saving dashboard data:', error);
    }
  }

  // Save analytics data
  async saveAnalyticsData() {
    try {
      const analyticsArray = Array.from(this.analyticsData.values());
      await AsyncStorage.setItem('dashboard_analytics', JSON.stringify(analyticsArray));
    } catch (error) {
      console.error('❌ Error saving analytics data:', error);
    }
  }

  // Initialize Dashboard Integration (legacy method for compatibility)
  async initializeDashboardIntegration(candidateId) {
    try {
      const dashboardId = `dashboard_${candidateId}`;
      
      const dashboard = {
        dashboardId,
        candidateId,
        widgets: await this.createDashboardWidgets(candidateId),
        analytics: await this.initializeAnalytics(candidateId),
        realTimeData: await this.initializeRealTimeData(candidateId),
        preferences: await this.loadDashboardPreferences(candidateId),
        lastUpdated: new Date().toISOString(),
        status: 'active'
      };

      this.dashboardData.set(dashboardId, dashboard);
      await this.saveDashboardData();

      // Start real-time updates
      await this.startRealTimeUpdates(dashboardId);

      return dashboard;
    } catch (error) {
      console.error('Error initializing dashboard integration:', error);
      throw new Error('Failed to initialize dashboard integration');
    }
  }

  // Create Dashboard Widgets (legacy method)
  async createDashboardWidgets(candidateId) {
    return {
      profileOverview: {
        id: 'profile_overview',
        type: 'profile_card',
        title: 'Profile Overview',
        data: await this.getProfileOverviewData(candidateId),
        enabled: true
      },
      interviewProgress: {
        id: 'interview_progress',
        type: 'progress_chart',
        title: 'Interview Progress',
        data: await this.getInterviewProgressData(candidateId),
        enabled: true
      }
    };
  }

  // Get profile overview data (legacy method)
  async getProfileOverviewData(candidateId) {
    return {
      candidateId,
      profileCompleteness: 85,
      totalInterviews: this.getTotalInterviews(),
      averageScore: this.calculateAverageScore(0)
    };
  }

  // Get interview progress data (legacy method)
  async getInterviewProgressData(candidateId) {
    return {
      totalInterviews: this.getTotalInterviews(),
      completedInterviews: this.getTotalInterviews(),
      averageScore: this.calculateAverageScore(0),
      progressTrend: 'improving'
    };
  }

  // Initialize real-time data (legacy method)
  async initializeRealTimeData(candidateId) {
    return {
      candidateId,
      lastUpdate: new Date().toISOString(),
      metrics: this.getMetrics(candidateId)
    };
  }

  // Load dashboard preferences (legacy method)
  async loadDashboardPreferences(candidateId) {
    try {
      const preferences = await AsyncStorage.getItem(`dashboard_preferences_${candidateId}`);
      return preferences ? JSON.parse(preferences) : {
        theme: 'light',
        notifications: true,
        autoRefresh: true
      };
    } catch (error) {
      console.error('Error loading dashboard preferences:', error);
      return {
        theme: 'light',
        notifications: true,
        autoRefresh: true
      };
    }
  }

  // Reset service
  reset() {
    this.dashboardData.clear();
    this.realTimeUpdates.clear();
    this.analyticsData.clear();
    this.subscribers.clear();
    
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    
    this.isInitialized = false;
  }

  // Cleanup
  cleanup() {
    this.reset();
  }
}

export default DashboardIntegrationService;
